import bs from 'binary-search';
import { observable, IObservableArray, action, makeObservable, toJS } from 'mobx';

export type BaseItemShape = {
    id: number;
};

export type ComparatorReturns = -1 | 0 | 1;

export type ItemComparator<ItemShape extends BaseItemShape> = (
    left: ItemShape,
    right: ItemShape
) => ComparatorReturns;
export type Comparator = (left: any, right: any) => ComparatorReturns;

export type GroupingStrategy<ItemShape extends BaseItemShape> = (item: ItemShape) => string;

export type FilterDirective<ItemShape extends BaseItemShape> = (item: ItemShape) => boolean;

export type SortingDirective<ItemShape extends BaseItemShape> = {
    getSortValue: (item: ItemShape) => any;
    comparator: Comparator;
};

type GroupingDirectiveBase<ItemShape extends BaseItemShape> = {
    getGroupValue: GroupingStrategy<ItemShape>;
    comparator: Comparator;
};

export type GroupingDirective<ItemShape extends BaseItemShape> =
    | GroupingDirectiveBase<ItemShape>
    | ({
          groupSelectedItems: true;
          selectedItemsGroupName: string;
      } & GroupingDirectiveBase<ItemShape>);

type LogEvent = 'INEFFICIENT_LOOKUP';

export type ListStoreOptions<ItemShape extends BaseItemShape> = {
    initialData?: ItemShape[];
    filterDirective?: FilterDirective<ItemShape>;
    sortingDirective?: SortingDirective<ItemShape>;
    defaultSortingDirective?: SortingDirective<ItemShape>;
    groupingDirective?: GroupingDirective<ItemShape>;
    logCallback?: (logEvent: LogEvent) => void;
};

type Data<ItemShape extends BaseItemShape> = {
    mode: 'LIST' | 'GROUPED_LIST';
    list: IObservableArray<ItemShape>;
    groupHeaders: IObservableArray<string>;
    groupCounts: IObservableArray<number>;
};

function indexByIdAndMakeObservable<ItemShape extends BaseItemShape>(
    items: ItemShape[]
): { [id: number]: ItemShape } {
    const map: { [id: number]: ItemShape } = {};
    for (const item of items) {
        map[item.id] = observable(item);
    }
    return map;
}

function configurationExists(configuration: any, message: string): asserts configuration {
    if (!configuration) {
        throw new Error(message);
    }
}

const defaultIdSortDirective: SortingDirective<BaseItemShape> = {
    getSortValue: (item: BaseItemShape) => item.id,
    comparator: (left: string, right: string) => (left < right ? -1 : right < left ? 1 : 0),
};

const correctIndexToInsert = (suggestedIndex: number): number => {
    if (suggestedIndex >= 0) {
        throw new Error('correctIndexToInsert unexpectedly called with suggestedIndex >= 0');
    }
    // pasted from https://github.com/darkskyapp/binary-search
    // If the value is not in the array, then -(index + 1) is returned, where index is where
    // the value should be inserted into the array to maintain sorted order.
    return suggestedIndex * -1 - 1;
};

const replaceObjectValues = (
    original: Record<string, any>,
    replacement: Record<string, any>
): void => {
    for (const [key, value] of Object.entries(replacement)) {
        if (key === 'id') {
            continue;
        }
        // only replace values that aren't equal to minimize reactions
        if (original[key] !== value) {
            original[key] = value;
        }
    }
    for (const [key, value] of Object.entries(original)) {
        if (key === 'id') {
            continue;
        }
        // eslint-disable-next-line no-prototype-builtins
        if (!replacement.hasOwnProperty(key) && value !== undefined && value !== null) {
            original[key] = undefined;
        }
    }
};

/**
 * We always include an object's id property as a sort criteria so that sorts will be deterministic (i.e. if sorting by first name,
 *   and two people have the same first name, we still want a deterministic order/position for these two people in a list).
 */
const createItemComparatorFromDirectives = <ItemShape extends BaseItemShape>(
    defaultSortingDirective: SortingDirective<ItemShape>,
    sortingDirective?: SortingDirective<ItemShape>,
    groupingDirective?: GroupingDirective<ItemShape>
): ItemComparator<ItemShape> => {
    return (left: ItemShape, right: ItemShape): -1 | 0 | 1 => {
        if (left.id === right.id) {
            return 0;
        }
        if (groupingDirective) {
            const groupSortComparatorResult = groupingDirective.comparator(
                groupingDirective.getGroupValue(left),
                groupingDirective.getGroupValue(right)
            );
            if (groupSortComparatorResult !== 0) {
                return groupSortComparatorResult;
            }
        }
        if (sortingDirective) {
            const baseSortComparatorResult = sortingDirective.comparator(
                sortingDirective.getSortValue(left),
                sortingDirective.getSortValue(right)
            );
            if (baseSortComparatorResult !== 0) {
                return baseSortComparatorResult;
            }
        }
        return defaultSortingDirective.comparator(
            defaultSortingDirective.getSortValue(left),
            defaultSortingDirective.getSortValue(right)
        );
    };
};

export class ListStore<ItemShape extends BaseItemShape> {
    private itemsById: { [id: string]: ItemShape } = {};
    @observable
    public readonly data: Data<ItemShape> = {
        mode: 'LIST',
        list: observable.array(),
        groupHeaders: observable.array(),
        groupCounts: observable.array(),
    };

    @observable
    public readonly selectedItems: { [id: string]: true } = {};

    private itemComparator: ItemComparator<ItemShape>;
    private defaultSortingDirective: SortingDirective<ItemShape>;
    private filterDirective: ListStoreOptions<ItemShape>['filterDirective'] = undefined;
    private sortingDirective: ListStoreOptions<ItemShape>['sortingDirective'] = undefined;
    private groupingDirective: ListStoreOptions<ItemShape>['groupingDirective'] = undefined;
    private logCallback: ListStoreOptions<ItemShape>['logCallback'];

    constructor(options?: ListStoreOptions<ItemShape>) {
        makeObservable(this);
        this.defaultSortingDirective = options?.defaultSortingDirective ?? defaultIdSortDirective;
        this.itemComparator = createItemComparatorFromDirectives(this.defaultSortingDirective);
        if (options) {
            this.filterDirective = options.filterDirective;
            this.sortingDirective = options.sortingDirective;
            this.groupingDirective = this.createGroupingDirective(options.groupingDirective);
            this.logCallback = options.logCallback;
            this.setItems(options.initialData ?? []);
        }
    }

    /**
     * This method completely replaces the contents of this.data.list. This method has the highest runtime of any
     *   operation in all of ListStore. It should be called when any of the following occurs:
     *   - The store's items are completely replaced (call to ListStore#setItems)
     *   - The store's filter criteria change (call to ListStore#setFilterDirective)
     *   - The store's sorting criteria change (call to ListStore#setSortingDirective)
     *   - The store's grouping criteria change (call to ListStore#setGroupingDirective)
     */
    @action
    private recomputeList(): void {
        this.itemComparator = createItemComparatorFromDirectives(
            this.defaultSortingDirective,
            this.sortingDirective,
            this.groupingDirective
        );
        let localItems: ItemShape[] = Object.values(this.itemsById);
        this.data.groupHeaders.replace([]);
        this.data.groupCounts.replace([]);
        const filterDirective = this.filterDirective;
        if (filterDirective) {
            localItems = localItems.filter((item) => filterDirective(item));
        }
        localItems.sort(this.itemComparator);
        this.data.list.replace(localItems);
        if (this.groupingDirective) {
            this.data.mode = 'GROUPED_LIST';
            if (localItems.length > 0) {
                let isFirstItem: boolean = true;
                let lastHeader: string = this.groupingDirective.getGroupValue(localItems[0]);
                let counter: number = 1;
                for (const localItem of localItems) {
                    if (isFirstItem) {
                        isFirstItem = false;
                        continue;
                    }
                    const itemHeader = this.groupingDirective.getGroupValue(localItem);
                    if (itemHeader === lastHeader) {
                        counter++;
                        continue;
                    } else {
                        this.data.groupHeaders.push(lastHeader);
                        this.data.groupCounts.push(counter);
                        lastHeader = itemHeader;
                        counter = 1;
                    }
                }
                this.data.groupHeaders.push(lastHeader);
                this.data.groupCounts.push(counter);
            }
        } else {
            this.data.mode = 'LIST';
        }
    }

    private itemPassesFilter(item: ItemShape): boolean {
        if (this.filterDirective) {
            return this.filterDirective(item);
        }
        return true;
    }

    private addItemToList(newItem: ItemShape): void {
        const list = this.data.list;
        const suggestedInsertIndex = bs(list, newItem, this.itemComparator);
        if (suggestedInsertIndex > -1) {
            throw new Error('unexpectedly found item already in list');
        }
        const insertIndex = correctIndexToInsert(suggestedInsertIndex);
        list.splice(insertIndex, 0, this.itemsById[newItem.id]);
        // if our list is in grouped mode, we have to make the necessary changes to groupHeaders and groupCounts
        if (this.groupingDirective) {
            const groupHeader = this.groupingDirective.getGroupValue(newItem);
            const suggestedGroupHeaderIndex = this.getGroupHeaderIndex(groupHeader);
            // the group is new, so create it
            if (suggestedGroupHeaderIndex < 0) {
                const correctedGroupHeaderIndex = correctIndexToInsert(suggestedGroupHeaderIndex);
                this.data.groupHeaders.splice(correctedGroupHeaderIndex, 0, groupHeader);
                this.data.groupCounts.splice(correctedGroupHeaderIndex, 0, 1);
                // the group exists, so increment the groupCount
            } else {
                this.data.groupCounts[suggestedGroupHeaderIndex]++;
            }
        }
    }

    private maybeShiftItemToNewPositionInList(
        existingItem: ItemShape,
        newItem: ItemShape,
        modifier?: (existingItem: ItemShape) => void
    ): void {
        // if anything that affects sort position has changed, we perform the requisite
        // shift of the item
        if (
            modifier ||
            (this.sortingDirective &&
                this.sortingDirective.getSortValue(existingItem) !==
                    this.sortingDirective.getSortValue(newItem)) ||
            // groups function as sort criteria and so can affect sort position
            (this.groupingDirective &&
                this.groupingDirective.getGroupValue(existingItem) !==
                    this.groupingDirective.getGroupValue(newItem))
        ) {
            this.removeItemFromList(existingItem);
            if (modifier) {
                modifier(existingItem);
            }
            this.addItemToList(newItem);
        }
    }

    private removeItemFromList(item: ItemShape): void {
        // If the item doesn't pass the filter, then it shouldn't be in the list and we can bail
        if (!this.itemPassesFilter(item)) {
            return;
        }
        const currentIndex = bs(this.data.list, item, this.itemComparator);
        if (currentIndex < 0) {
            // it's possible the element wasn't found in the binary search because it wasn't where we expected it based on the itemComparator
            // fallback to linear search
            const inefficientCurrentIndex = this.data.list.findIndex(
                (listItem) => listItem.id === item.id
            );
            this.logCallback?.('INEFFICIENT_LOOKUP');
            if (inefficientCurrentIndex === -1) {
                throw new Error(
                    'unexpectedly did not find item in this.data.items during inefficient lookup'
                );
            } else {
                this.data.list.splice(inefficientCurrentIndex, 1);
            }
        } else {
            this.data.list.splice(currentIndex, 1);
        }
        // if our list is in grouped mode, we have to make the necessary changes to groupHeaders and groupCounts
        if (this.groupingDirective) {
            const groupHeader = this.groupingDirective.getGroupValue(item);
            const groupHeaderIndex = this.getGroupHeaderIndex(groupHeader);
            const currentGroupCount = this.data.groupCounts[groupHeaderIndex];
            // the groups only item is being removed, so we remove the group altogether
            if (currentGroupCount === 1) {
                this.data.groupCounts.splice(groupHeaderIndex, 1);
                this.data.groupHeaders.splice(groupHeaderIndex, 1);
                // the group still has items remaining so we just decrement its groupCount
            } else {
                this.data.groupCounts[groupHeaderIndex]--;
            }
        }
    }

    private getGroupHeaderIndex(groupHeader: string): number {
        configurationExists(
            this.groupingDirective,
            'this.groupingDirective was unexpectedly undefined'
        );
        return bs(this.data.groupHeaders, groupHeader, this.groupingDirective.comparator);
    }

    @action
    public setItems(items: ItemShape[]): void {
        this.itemsById = indexByIdAndMakeObservable(items);
        this.recomputeList();
    }

    @action
    public upsertItems(newItems: ItemShape[]): void {
        // Plain English
        // A) If we have the item already
        //  A1) If it didn't pass the filter before and still doesn't, update the value and bail
        //  A2) If it did pass the filter before, but doesn't now, remove it from the list
        //  A3) If it didn't pass filter before, but does now, add it to the list
        //  A4) If it passed before and now, maybe shift its position in the list
        // B) If we don't have the item already
        //  B1) Always add the item to our backing cache
        //  B2) If it doesn't pass the filter, bail out
        //  B3) If it passes the filter, add it to the list
        for (const newItem of newItems) {
            // A) If we have the item already
            if (this.hasItem(newItem.id)) {
                const existingItem = this.itemsById[newItem.id];
                const existingItemPassesFilter = this.itemPassesFilter(existingItem);
                const newItemPassesFilter = this.itemPassesFilter(newItem);
                // A1) If it didn't pass the filter before and still doesn't, update the value and bail
                if (!existingItemPassesFilter && !newItemPassesFilter) {
                    replaceObjectValues(existingItem, newItem);
                    continue;
                    // A2) If it did pass the filter before, but doesn't now, remove it
                } else if (existingItemPassesFilter && !newItemPassesFilter) {
                    this.removeItemFromList(existingItem);
                    // A3) If it didn't pass filter before, but does now, add it
                } else if (!existingItemPassesFilter && newItemPassesFilter) {
                    this.addItemToList(newItem);
                    // A4) If it passed before and now, maybe shift it
                } else {
                    this.maybeShiftItemToNewPositionInList(existingItem, newItem);
                }
                replaceObjectValues(existingItem, newItem);
                // B) If we don't have the item already
            } else {
                // B1) Always add the item to our backing cache
                this.itemsById[newItem.id] = observable(newItem);
                const newItemPassesFilter = this.itemPassesFilter(newItem);
                //  B2) If it doesn't pass the filter, bail out
                if (!newItemPassesFilter) {
                    continue;
                }
                //  B3) If it passes the filter, add it to the list
                this.addItemToList(newItem);
            }
        }
    }

    @action
    public removeItems(itemIds: ItemShape['id'][]): void {
        for (const itemId of itemIds) {
            if (!this.hasItem(itemId)) {
                continue;
            }
            const item = this.itemsById[itemId];
            this.removeItemFromList(item);
            delete this.itemsById[itemId];
            if (this.selectedItems[itemId]) {
                this.toggleItemSelection(itemId);
            }
        }
    }

    /**
     * check if given id exist in listStore (may or may not matched current filter directive)
     */
    public hasItem(id: ItemShape['id']): boolean {
        return !!this.itemsById[id];
    }

    public getItemById(id: ItemShape['id']): ItemShape | void {
        return toJS(this.itemsById[id]);
    }

    /**
     * get item index for given id among all 'visible' items (items that matched current filter directive)
     */
    public getItemIndex(id: ItemShape['id']): number | void {
        if (!this.hasItem(id)) {
            return undefined;
        }
        const index = bs(this.data.list, this.itemsById[id], this.itemComparator);
        if (index < 0) {
            return undefined;
        } else {
            return index;
        }
    }

    @action
    public toggleItemSelection(id: ItemShape['id']): void {
        if (this.hasItem(id)) {
            if (this.selectedItems[id]) {
                if (this.groupingDirective && 'groupSelectedItems' in this.groupingDirective) {
                    this.maybeShiftItemToNewPositionInList(
                        this.itemsById[id],
                        this.itemsById[id],
                        () => {
                            delete this.selectedItems[id];
                        }
                    );
                } else {
                    delete this.selectedItems[id];
                }
            } else {
                if (this.groupingDirective && 'groupSelectedItems' in this.groupingDirective) {
                    this.maybeShiftItemToNewPositionInList(
                        this.itemsById[id],
                        this.itemsById[id],
                        () => {
                            this.selectedItems[id] = true;
                        }
                    );
                } else {
                    this.selectedItems[id] = true;
                }
            }
        } else if (this.selectedItems[id]) {
            delete this.selectedItems[id];
        }
    }

    @action
    public setSelectedItems(ids: ItemShape['id'][]): void {
        const idsMap: { [id: string]: true } = {};
        for (const id of ids) {
            idsMap[id] = true;
        }
        for (const existingSelectedId of Object.keys(this.selectedItems)) {
            if (!idsMap[existingSelectedId]) {
                this.toggleItemSelection(parseInt(existingSelectedId, 10));
            }
        }
        for (const id of ids) {
            if (this.hasItem(id) && !this.selectedItems[id]) {
                this.toggleItemSelection(id);
            }
        }
    }

    @action
    public setFilterDirective(filterDirective?: FilterDirective<ItemShape>): void {
        this.filterDirective = filterDirective;
        this.recomputeList();
    }

    @action
    public setSortingDirective(sortingDirective?: SortingDirective<ItemShape>): void {
        this.sortingDirective = sortingDirective;
        this.recomputeList();
    }

    private createGroupingDirective(
        groupingDirective?: GroupingDirective<ItemShape>
    ): GroupingDirective<ItemShape> | undefined {
        let localGroupingDirective: GroupingDirective<ItemShape> | undefined = undefined;
        if (!groupingDirective) {
            return undefined;
        }
        if (typeof groupingDirective !== 'undefined' && 'groupSelectedItems' in groupingDirective) {
            localGroupingDirective = {
                getGroupValue: (item: ItemShape) => {
                    return this.selectedItems[item.id]
                        ? groupingDirective['selectedItemsGroupName']
                        : groupingDirective.getGroupValue(item);
                },
                comparator: (left: string, right: string) => {
                    if (
                        left === groupingDirective.selectedItemsGroupName &&
                        right !== groupingDirective.selectedItemsGroupName
                    ) {
                        return -1;
                    } else if (
                        right === groupingDirective.selectedItemsGroupName &&
                        left !== groupingDirective.selectedItemsGroupName
                    ) {
                        return 1;
                    } else if (
                        right === groupingDirective.selectedItemsGroupName &&
                        left === groupingDirective.selectedItemsGroupName
                    ) {
                        return 0;
                    } else {
                        return groupingDirective.comparator(left, right);
                    }
                },
                selectedItemsGroupName: groupingDirective.selectedItemsGroupName,
                groupSelectedItems: groupingDirective.groupSelectedItems,
            };
        }
        return localGroupingDirective ?? groupingDirective;
    }

    @action
    public setGroupingDirective(groupingDirective?: GroupingDirective<ItemShape>): void {
        this.groupingDirective = this.createGroupingDirective(groupingDirective);
        this.recomputeList();
    }
}
